import { useCallback, useState } from 'react';
import { CrudPage } from '@/components/CrudPage';
import { Badge } from '@/components/ui';
import { formatDateTime, formatPhone, formatRelative } from '@/lib/format';

interface Lead {
  id: string;
  vicidial_lead_id: string | null;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  campaign: string | null;
  source_service: string | null;
  do_not_call: boolean;
  last_called_at: string | null;
  created_at: string;
}

import LeadPurge from '@/components/LeadPurge';

export default function Leads() {
  const [total, setTotal] = useState<number | null>(null);
  // Stable, so `DataTable`'s effect does not re-fire on every render.
  const onTotal = useCallback((value: number) => setTotal(value), []);

  return (
    <CrudPage<Lead>
      title="Leads"
      description="Records mirrored from the dialer, plus anything created natively."
      onTotal={onTotal}
      extraActions={
        <>
          {/* The count, where somebody asking "how many leads do I have"
              looks. It existed only in the pager, so a page holding all of
              them showed no number at all. */}
          {total === null ? null : (
            <span className="mr-1 text-sm text-ink-500">
              <strong className="text-ink-900 dark:text-ink-50">
                {total.toLocaleString()}
              </strong>{' '}
              leads
            </span>
          )}
          <LeadPurge />
        </>
      }
      resource="leads"
      path="/leads"
      queryKey="leads"
      searchPlaceholder="Phone, name or lead ID…"
      emptyTitle="No leads yet"
      emptyDescription="Leads are created automatically as calls arrive."
      filters={[
        {
          key: 'do_not_call',
          label: 'DNC',
          options: [
            { value: 'true', label: 'Suppressed' },
            { value: 'false', label: 'Callable' },
          ],
        },
      ]}
      fields={[
        {
          name: 'phone',
          label: 'Phone',
          required: true,
          immutable: true,
          placeholder: '5555550100',
        },
        { name: 'first_name', label: 'First name' },
        { name: 'last_name', label: 'Last name' },
        { name: 'email', label: 'Email', type: 'email' },
        { name: 'city', label: 'City' },
        { name: 'state', label: 'State' },
        { name: 'postal_code', label: 'ZIP' },
        {
          name: 'campaign',
          label: 'Campaign',
          immutable: true,
          // The campaign comes from the dialer's list. Changing it here would
          // not move the lead in Vicidial, so it would only be a local lie.
          hint: 'Set by the list this lead was imported from.',
        },
        {
          name: 'source_service',
          label: 'Service from the list',
          hint: 'What the list says they enquired about. The bot still confirms it in conversation.',
        },
      ]}
      columns={[
        {
          key: 'phone',
          header: 'Lead',
          render: (lead) => (
            <div>
              <div className="font-mono text-sm">{formatPhone(lead.phone)}</div>
              <div className="text-xs text-ink-500">
                {[lead.first_name, lead.last_name].filter(Boolean).join(' ') || '—'}
              </div>
            </div>
          ),
        },
        {
          key: 'location',
          header: 'Location',
          render: (lead) => (
            <span className="text-xs text-ink-600 dark:text-ink-300">
              {[lead.city, lead.state, lead.postal_code].filter(Boolean).join(', ') || '—'}
            </span>
          ),
        },
        {
          key: 'source_service',
          header: 'Enquired about',
          render: (lead) => lead.source_service ?? <span className="text-ink-400">—</span>,
        },
        {
          key: 'campaign',
          header: 'Campaign',
          render: (lead) => lead.campaign ?? <span className="text-ink-400">—</span>,
        },
        {
          key: 'dnc',
          header: 'DNC',
          render: (lead) =>
            lead.do_not_call ? <Badge tone="red">Suppressed</Badge> : <span className="text-ink-400">—</span>,
        },
        {
          key: 'last_called',
          header: 'Last called',
          render: (lead) => (
            <span className="text-xs text-ink-500">{formatRelative(lead.last_called_at)}</span>
          ),
        },
        {
          key: 'created_at',
          header: 'Created',
          render: (lead) => (
            <span className="text-xs text-ink-500">{formatDateTime(lead.created_at)}</span>
          ),
        },
      ]}
    />
  );
}
